import React, { FC, Fragment } from 'react';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Switch,
    FormControl,
    FormControlLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Organization } from 'models/organization';
import { LinkedOrganization, Role } from 'models/user';

type LinkedOrganizationProps = {
    linkedOrganizations: LinkedOrganization[];
    roles: Role[];
    onActiveChange: (organization: LinkedOrganization, isActive: boolean) => void;
    onRoleChange: (organization: LinkedOrganization, role: string) => void;
};

const LinkedOrganizationList: FC<LinkedOrganizationProps> = ({
    linkedOrganizations,
    roles,
    onActiveChange,
    onRoleChange,
}: LinkedOrganizationProps) => {
    const { t: translate } = useTranslation(['common']);

    const isRoleInList = (roleName: string) => roles.some((role) => role.role === roleName);

    return (
        <Fragment>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('common:ORGANIZATION')}</TableCell>
                            <TableCell>{translate('common:ROLE')}</TableCell>
                            <TableCell>{translate('common:ACTIVE')}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {linkedOrganizations ? (
                            <Fragment>
                                {linkedOrganizations.map((lOrg: LinkedOrganization) => (
                                    <TableRow key={(lOrg.organization as Organization)._id} hover>
                                        <TableCell>{(lOrg.organization as Organization).name}</TableCell>
                                        <TableCell>
                                            <FormControl margin="dense" fullWidth>
                                                <Select
                                                    id="linked-organization-role"
                                                    name="linkedOrganizationRole"
                                                    size="small"
                                                    value={lOrg.role}
                                                    onChange={(event: SelectChangeEvent) =>
                                                        onRoleChange(lOrg, event.target.value)
                                                    }
                                                >
                                                    {!isRoleInList(lOrg.role) && (
                                                        <MenuItem value={lOrg.role}>
                                                            {translate(`common:${lOrg.role}`)}
                                                        </MenuItem>
                                                    )}
                                                    {roles.map((role: Role) => (
                                                        <MenuItem key={role._id} value={role.role}>
                                                            {translate(`common:${role.role}`)}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl margin="dense" fullWidth>
                                                <FormControlLabel
                                                    id="isActive"
                                                    name="isActive"
                                                    control={
                                                        <Switch
                                                            color="secondary"
                                                            checked={lOrg.active}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                                onActiveChange(lOrg, event.target.checked)
                                                            }
                                                        />
                                                    }
                                                    label={
                                                        lOrg.active
                                                            ? translate('common:ACTIVE')
                                                            : translate('common:INACTIVE')
                                                    }
                                                />
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </Fragment>
                        ) : undefined}
                    </TableBody>
                </Table>
            </TableContainer>
        </Fragment>
    );
};

export default LinkedOrganizationList;
